import cn from 'classnames';
import FunnelCart from 'components/funnel/cart';
import FunnelCartBox from 'components/funnel/cart-box';
import FunnelHeader from 'components/funnel/header';
import { useCart } from 'contexts/cart';
import useMOVConditions from 'hooks/common/use-mov-conditions';
import Restricted from 'modules/common/restricted';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { FC, useState } from 'react';
import { useIntl } from 'react-intl';
import { FUNNEL_STEPS } from './constants';
import { Progress } from './progress';
import { useSubscriptionFunnel } from './state';
import StepConfirmation from './step-confirmation';
import StepOverview from './step-overview';
import StepPreference from './step-preference';
import StepProducts from './step-products';
import StepStart from './step-start';
import { useGetHomeLink } from 'hooks/common/use-get-home-link';

const SubscriptionFunnel: FC = () => {
  const { step, isExistingSubscription, isSingleProductPurchase } = useSubscriptionFunnel();
  const { items, isCartReady, effectiveCart } = useCart();
  const { push } = useRouter();
  const homeLink = useGetHomeLink();
  const intl = useIntl();

  const [isCartExpanded, setCartExpanded] = useState(false);
  const { shouldShowMinimumBasketNotice } = useMOVConditions({
    effectiveCart,
    isInCheckout: true,
  });

  const subscriptionFunnel = (
    <div>
      <Head>
        <title>{intl.formatMessage({ id: 'subscription-funnel:page-title' })}</title>
      </Head>
      <FunnelHeader title="Das Pets Deli Futterabo" onLogoClick={() => push(homeLink)}>
        {step !== FUNNEL_STEPS.START && step !== FUNNEL_STEPS.PREFERENCE && (
          <FunnelCart
            setCartExpanded={setCartExpanded}
            isCartExpanded={isCartExpanded}
            hasExistingSub={isExistingSubscription}
          />
        )}
      </FunnelHeader>
      <div
        className={cn(
          'lg:bg-grey-100 flex-col pb-16! lg:flex lg:items-center lg:justify-center',
          items.length > 0 && step !== FUNNEL_STEPS.START && step !== FUNNEL_STEPS.PREFERENCE
            ? 'pt-28 lg:py-8'
            : 'pt-12 lg:py-8',
          shouldShowMinimumBasketNotice && 'lg:pb-20!'
        )}
      >
        {step === FUNNEL_STEPS.PRODUCTS || step === FUNNEL_STEPS.OVERVIEW ? (
          <>
            {' '}
            <div className="flex flex-col lg:-mx-6 lg:flex-row lg:py-4">
              <div className="flex-1 lg:px-6">
                <div className="mx-auto w-full max-w-2xl">
                  <Progress />
                </div>
                {step === FUNNEL_STEPS.PRODUCTS && <StepProducts />}
                {step === FUNNEL_STEPS.OVERVIEW && <StepOverview />}
              </div>
              {isCartReady && (
                <FunnelCartBox
                  isCartExpanded={isCartExpanded}
                  canRemoveProducts
                  className="lg:sticky lg:top-6"
                  showMinumumBasketNotice={
                    shouldShowMinimumBasketNotice && !isSingleProductPurchase
                  }
                  hasExistingSubscription={isExistingSubscription}
                />
              )}
            </div>
          </>
        ) : (
          <>
            {step === FUNNEL_STEPS.PREFERENCE ||
              (step === FUNNEL_STEPS.CONFIRMATION && (
                <div className="mx-auto w-full max-w-2xl">
                  <Progress />
                </div>
              ))}
            {step === FUNNEL_STEPS.START && <StepStart />}
            {step === FUNNEL_STEPS.PREFERENCE && <StepPreference />}
            {step === FUNNEL_STEPS.CONFIRMATION && <StepConfirmation />}
          </>
        )}
      </div>
    </div>
  );

  if (isExistingSubscription) {
    return <Restricted>{subscriptionFunnel}</Restricted>;
  }

  return subscriptionFunnel;
};

export default SubscriptionFunnel;
